import React, { FC, memo, useEffect, useState } from 'react';

import { Grid } from '@components/Grid';
import { Loader } from '@components/Loader';
import { Space } from '@components/Space';
import { TextPlaceholder } from '@components/TextPlaceholder';
import { useGetHalalShopProductCard } from '@features/halalShop/catalog/hooks/useGetHalalShopProductCard';
import { shopProductsEndpoints } from '@features/halalShop/store/apiSerive';
import { ShopOrdersProduct } from '@features/halalShop/store/types';
import { useSearchParam } from '@hooks/useSearchParam';
import {
  PaginationLimitOffsetAdapter,
  calcNewOffset,
} from '@packages/uiKit/PaginationOffset';
import { CATALOG_ITEMS_PER_PAGE } from '@utils/globalContants';
import { StyledProductCardsGrid } from './styles';

interface ProductCardsGridProps {
  className?: string;
  style?: React.CSSProperties;
}

const _ProductCardsGrid: FC<ProductCardsGridProps> = (props) => {
  const { ...restProps } = props;

  const [data, setData] = useState<ShopOrdersProduct[]>();

  const [pageIndex, setPageIndex] = useSearchParam<string>({
    paramName: 'page',
    defaultValue: '1',
  });

  const newOffset = calcNewOffset(Number(pageIndex), CATALOG_ITEMS_PER_PAGE);

  const [offset, setOffset] = useState<number>(0);

  const [elementsCount, setElementsCount] = useState<number>(0);

  useEffect(() => {
    setOffset(newOffset);
  }, [newOffset, pageIndex, setOffset]);

  const [getProductsQ, { isSuccess, isLoading, isFetching }] =
    shopProductsEndpoints.useLazyGetProductsQuery();

  useEffect(() => {
    const fn = async () => {
      const res = await getProductsQ({
        limit: CATALOG_ITEMS_PER_PAGE,
        offset: offset,
      }).unwrap();

      setElementsCount(res.count);
      setData(res.results);
    };

    fn();
  }, [getProductsQ, offset, pageIndex]);

  const isEmpty = data && data.length === 0;

  const items = useGetHalalShopProductCard(data, pageIndex);

  return (
    <StyledProductCardsGrid {...restProps}>
      {isLoading || isFetching ? (
        <Loader />
      ) : (
        <>
          {isSuccess ? (
            isEmpty ? (
              <TextPlaceholder variant="noData" />
            ) : (
              <>
                <Grid cols={4} border="none" columnGap={25} rowGap={25}>
                  {items}
                </Grid>
                <Space size={40} />
                <PaginationLimitOffsetAdapter
                  offset={offset}
                  setPageIndex={setPageIndex}
                  pageIndex={pageIndex}
                  setOffset={setOffset}
                  totalElementsCount={elementsCount}
                  perPage={CATALOG_ITEMS_PER_PAGE}
                />
              </>
            )
          ) : (
            <TextPlaceholder variant="error" />
          )}
        </>
      )}
    </StyledProductCardsGrid>
  );
};

export const ProductCardsGrid = memo(_ProductCardsGrid);
